import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { ParentMascot } from "@/components/brand/ParentMascot";
import { cn } from "@/lib/utils/cn";

type Props = {
  open: boolean;
  onPick: (relation: "Mama" | "Tata") => Promise<void>;
};

/**
 * Pierwszy onboarding rodzica-założyciela — przed manifestem marki.
 * Pyta "kim jesteś" (Mamą / Tatą) i zapisuje wybór do member.relation.
 * Po wyborze automatycznie zamyka się i pokazuje parent-philosophy moment
 * z poprawną maskotką (gender wynika z relation).
 *
 * Mountowany w AppShell, sticky — nie da się zamknąć bez wyboru. Tylko dla
 * isAdmin parent bez ustawionej relation. Pojawia się raz na życie konta.
 */
export function RoleSetupDialog({ open, onPick }: Props) {
  const [picked, setPicked] = useState<"f" | "m" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const submit = async () => {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onPick(picked === "f" ? "Mama" : "Tata");
      // Sukces — parent kontener odmontuje dialog (zmieniony stan member)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Nie udało się zapisać");
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-setup-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-bg p-6 animate-in fade-in duration-300"
    >
      <div className="w-full max-w-sm flex flex-col items-center">
        <h2
          id="role-setup-title"
          className="font-display text-[24px] font-extrabold tracking-tight text-navy text-center"
        >
          Powiedz, kim jesteś
        </h2>
        <p className="mt-2 text-sm text-slate text-center max-w-[28ch]">
          Dzieci zobaczą Cię jako Mamę lub Tatę w aplikacji.
        </p>

        <div className="mt-8 grid grid-cols-2 gap-3 w-full">
          <RoleOption
            gender="f"
            label="Mamą"
            selected={picked === "f"}
            onSelect={() => setPicked("f")}
          />
          <RoleOption
            gender="m"
            label="Tatą"
            selected={picked === "m"}
            onSelect={() => setPicked("m")}
          />
        </div>

        {error && (
          <p className="mt-4 text-sm text-danger text-center">{error}</p>
        )}

        <div className="mt-8 w-full max-w-[260px]">
          <Button
            variant="accent"
            size="lg"
            block
            disabled={!picked || busy}
            onClick={submit}
          >
            {busy ? "Zapisywanie…" : "Tak, jestem"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RoleOption({
  gender,
  label,
  selected,
  onSelect,
}: {
  gender: "f" | "m";
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "rounded-[20px] border-2 p-4 transition-all flex flex-col items-center gap-2",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2",
        selected
          ? "border-green bg-green/5 shadow-sm"
          : "border-line bg-card hover:border-slate/30",
      )}
    >
      <div
        className="transition-[filter] duration-200"
        style={{
          filter: selected ? "none" : "grayscale(100%) opacity(0.55)",
        }}
      >
        <ParentMascot gender={gender} size={188} shadow={false} />
      </div>
      <div
        className={cn(
          "text-[15px] font-extrabold transition-colors",
          selected ? "text-forest" : "text-slate",
        )}
      >
        Jestem {label}
      </div>
    </button>
  );
}
